import React, { useState, forwardRef, useEffect, useRef } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import Joi from "joi-browser";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ClaimantForm.css";
import arrow from "../../assets/Icons/PNG/orange-arrow.png";
import "react-datepicker/dist/react-datepicker.css";
import MaskedInput from "../../components/MaskedInput";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { FileInput } from "../../components/FileInput/FileInput";
import { datePattern, emailPattern, phonePattern, ssnPattern, zipCodePattern } from "../../lookups/formPatterns";
import DatePicker from "react-datepicker";
import useAddressSuggestions from "../../hooks/useAddressSuggestions";

let today = new Date();
today.setHours(0, 0, 0, 0);

const schema = {
  ClaimantFirstName: Joi.string()
    .trim()
    .required()
    .label("Claimant First Name"),
  ClaimantLastName: Joi.string().trim().required().label("Claimant Last Name"),
  Alias: Joi.string()
    .trim()
    .optional()
    .allow("")
    .label("Alias(es) - List All that Apply"),
  SSN: Joi.string()
    .regex(ssnPattern)
    .required()
    .label("Last 4 or 9 Digits of SSN")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter exactly Last 4 or 9 Digits of SSN (e.g. 123456789 or 123-45-6789 or 6789)";
        }
        if (err.type === "any.required") {
          err.message = "Last 4 or 9 Digits of SSN is required.";
        }
        if (err.type === "string.empty") {
          err.message = "Last 4 or 9 Digits of SSN cannot be empty.";
        }
      });
      return errors;
    }),
  DateBirth: Joi.date().less("now").required().label("Date of Birth").error((errors) => {
    errors.forEach((err) => {
      if (err.type === "date.base") {
        err.message = "Date of Birth must be a valid date";
      }
    });
    return errors;
  }),
  ClaimantAddress: Joi.string()
    .trim()
    .required()
    .max(255)
    .label("Claimant Address"),
  city: Joi.string().trim().required().max(255).label("City"),
  state: Joi.string()
    .trim()
    .required()
    .length(2)
    .label("State"),
  zip: Joi.string()
    .required()
    .regex(zipCodePattern)
    .length(5)
    .label("Zip")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Zip Code!";
        }
      });
      return errors;
    }),
  ClaimantEmail: Joi.string()
    .trim()
    .email()
    .regex(emailPattern)
    .optional()
    .allow("")
    .label(
      "Claimant Email ** Strongly Recommended for Social Canvassing, but not required"
    )
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid E-mail Address";
        }
      });
      return errors;
    }),
  Phone: Joi.string()
    .trim()
    .regex(phonePattern)
    .optional()
    .allow("")
    .label(
      "Phone ** Strongly Recommended for Social Canvassing, but not required"
    )
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Phone Number";
        }
      });
      return errors;
    }),
  EmployerName: Joi.string().trim().optional().allow("").label("Employer Name"),
  EmployerAddress: Joi.string()
    .trim()
    .optional()
    .allow("")
    .max(255)
    .label("Employer Address"),
  EmployerCity: Joi.string().trim().optional().allow("").max(255).label("City"),
  EmployerState: Joi.string()
    .trim()
    .optional()
    .allow("")
    .length(2)
    .label("State"),
  EmployerZip: Joi.string()
    .optional()
    .allow("")
    .regex(zipCodePattern)
    .length(5)
    .label("Zip")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Zip Code!";
        }
      });
      return errors;
    }),
  EmployerPhone: Joi.string()
    .trim()
    .regex(phonePattern)
    .optional()
    .allow("")
    .label("Employer Phone")
    .error((errors) => {
      errors.forEach((err) => {
        if (err.type === "string.regex.base") {
          err.message = "Please enter a valid Phone Number";
        }
      });
      return errors;
    }),
  EmployerContactName: Joi.string()
    .trim()
    .optional()
    .allow("")
    .label("Employer Contact Name"),
  ClaimantPosition: Joi.string()
    .trim()
    .optional()
    .allow("")
    .label("Claimant's Position"),
  DateIncident: Joi.date().less("now").required().label("Date of Incident").error((errors) => {
    errors.forEach((err) => {
      if (err.type === "date.base") {
        err.message = "Date of Incident must be a valid date";
      }
    });
    return errors;
  }),
  LastWorked: Joi.date()
    .less("now")
    .optional()
    .allow(["", null])
    .label("Last Worked"),
  Nature: Joi.string()
    .trim()
    .required()
    .label("Nature/Extent of Alleged Injury/Loss"),
  AdditionalComments: Joi.string()
    .trim()
    .optional()
    .allow("")
    .label("Additional Comments"),
  CoverageType: Joi.string().trim().optional().allow("").label("Coverage Type"),
  DueDate: Joi.date().optional().allow([null, ""]).min(today).label("Due Date"),
};

const ClaimantForm = forwardRef(
  ({ formData, isAddressInvalid, setFormData, onUploadFile, uploadedFiles }, ref) => {
    const [formErrors, setFormErrors] = useState({});

    const {
      fetchAddressSuggestions: fetchClaimantSuggestions,
      setAddressSuggestions: setClaimantAddressSuggestions,
      isChoosedSuggestion: isChoosedClaimantSuggestion,
      setIsChoosedSuggestion: setIsChoosedClaimantSuggestion,
      renderSuggestionsDropdown: renderClaimantSuggestionsDropdown,
    } = useAddressSuggestions();

    const {
      fetchAddressSuggestions: fetchEmployerSuggestions,
      setAddressSuggestions: setEmployerAddressSuggestions,
      isChoosedSuggestion: isChoosedEmployerSuggestion,
      setIsChoosedSuggestion: setIsChoosedEmployerSuggestion,
      renderSuggestionsDropdown: renderEmployerSuggestionsDropdown,
    } = useAddressSuggestions();

    const isValidDateFormat = (dateString) => {
      // Check if the date string matches the format
      if (!datePattern.test(dateString)) {
          return false;
      }

      // Parse the date components
      const [_, month, day, year] = dateString.match(datePattern).map(Number);

      // Validate the date using JavaScript's Date object
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && 
            date.getMonth() === month - 1 && 
            date.getDate() === day;
    }

    const handleDateChange = (name, value) => {
      setFormData((prevData) => ({
        ...prevData,
        claimantInformation: {
          ...prevData.claimantInformation,
          [name]: value,
        },
      }));
    };
    
    const handleDateBlur = (name, e) => {
      const date = e?.target?.value;
      const isValidDate = isValidDateFormat(date);

      setFormData((prevData) => ({
        ...prevData,
        claimantInformation: {
          ...prevData.claimantInformation,
          [name]: isValidDate ? new Date(date) : "",
        },
      }));
    };

    const handleChange = (e) => {
      const { name, value } = e.target;

      setFormData((prevData) => ({
        ...prevData,
        claimantInformation: {
          ...prevData.claimantInformation,
          [name]: (name === "state" || name === "EmployerState") ? value.toUpperCase() : value,
        },
      }));

      if (name === "ClaimantAddress") {
        setIsChoosedClaimantSuggestion(false);
      } else if (name === "EmployerAddress") {
        setIsChoosedEmployerSuggestion(false);
      }
    };

    const handleSelectAddress = (suggestion, name) => {
      const displayAddress = suggestion.street_line;
      const displayState = suggestion.state;
      const displayCity = suggestion.city;
      const displayZipCode = suggestion.zipcode;

      if (name === "ClaimantAddress") {
        setFormData((prevData) => ({
          ...prevData,
          claimantInformation: {
            ...prevData.claimantInformation,
            ClaimantAddress: displayAddress,
            state: displayState,
            city: displayCity,
            zip: displayZipCode,
          },
        }));
        setClaimantAddressSuggestions([]);
        setIsChoosedClaimantSuggestion(true);
      } else if (name === "EmployerAddress") {
        setFormData((prevData) => ({
          ...prevData,
          claimantInformation: {
            ...prevData.claimantInformation,
            EmployerAddress: displayAddress,
            EmployerState: displayState,
            EmployerCity: displayCity,
            EmployerZip: displayZipCode,
          },
        }));
        setEmployerAddressSuggestions([]);
        setIsChoosedEmployerSuggestion(true);
      }
    };

    useEffect(() => {
      if (
        formData.ClaimantAddress === "" ||
        formData.ClaimantAddress?.trim().length < 1
      ) {
        setFormData((prevData) => ({
          ...prevData,
          claimantInformation: {
            ...prevData.claimantInformation,
            ClaimantAddress: "",
            state: "",
            city: "",
            zip: "",
          },
        }));
        setClaimantAddressSuggestions([]);
      }

      const searchTimeoutId = setTimeout(async () => {
        if (
          formData.ClaimantAddress.length > 2 &&
          !isChoosedClaimantSuggestion
        ) {
          await fetchClaimantSuggestions(formData.ClaimantAddress);
        } else {
          setClaimantAddressSuggestions([]);
        }
      }, 500);

      return () => clearTimeout(searchTimeoutId);
    }, [formData.ClaimantAddress, isChoosedClaimantSuggestion]);

    useEffect(() => {
      if (
        formData.EmployerAddress === "" ||
        formData.EmployerAddress?.trim().length < 1
      ) {
        setFormData((prevData) => ({
          ...prevData,
          claimantInformation: {
            ...prevData.claimantInformation,
            EmployerAddress: "",
            EmployerState: "",
            EmployerCity: "",
            EmployerZip: "",
          },
        }));
        setEmployerAddressSuggestions([]);
      }

      const searchTimeoutId = setTimeout(async () => {
        if (
          formData.EmployerAddress.length > 2 &&
          !isChoosedEmployerSuggestion
        ) {
          await fetchEmployerSuggestions(formData.EmployerAddress);
        } else {
          setEmployerAddressSuggestions([]);
        }
      }, 500);

      return () => clearTimeout(searchTimeoutId);
    }, [formData.EmployerAddress, isChoosedEmployerSuggestion]);

    const handleMaskedValueChange = (name, value) => {
      setFormData((prevData) => ({
        ...prevData,
        claimantInformation: {
          ...prevData.claimantInformation,
          [name]: value,
        },
      }));
    };

    const validateForm = () => {
      const result = Joi.validate(formData, schema, {
        abortEarly: false,
      });
      if (!result.error && !isAddressInvalid) return null;

      let errors = {};
      result.error?.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });

      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        errors = {...errors, ClaimantAddress: "The address information for Requestor and Claimant cannot be the same"}
      }

      return errors;
    };

    const validateField = (name) => {
      const result = Joi.validate(formData[name], schema[name], {
        abortEarly: false,
      });

      if (result.error) {
        const err = result.error.details[0].message
        setFormErrors(prevState => ({...prevState, [name]: err}))
      } else {
        setFormErrors(prevState => ({...prevState, [name]: ""}))
      }

      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        setFormErrors(prevState => ({...prevState, ClaimantAddress: "The address information for Requestor and Claimant cannot be the same"}))
      } else {
        setFormErrors(prevState => ({...prevState, ClaimantAddress: ""}))
      }
    };

    // Realtime form validation
    const prevFormDataState = useRef()
    useEffect(() => {
      if (prevFormDataState.current) {
        const changedKeys = Object.keys(formData).filter(
          key => prevFormDataState.current[key] !== formData[key]
        );

        if (changedKeys.length > 0) {
          for (let i = 0; i < changedKeys.length; i++) {
            validateField(changedKeys[i])
          }
        }
      }
      prevFormDataState.current = formData;
      
      // Show error if the Requestor address and the Claimant address are the same
      if (isAddressInvalid) {
        setFormErrors(prevState => ({...prevState, ClaimantAddress: "The address information for Requestor and Claimant cannot be the same"}))
      } else {
        setFormErrors(prevState => ({...prevState, ClaimantAddress: ""}))
      }
    }, [formData]);

    const handleSubmit = (e) => {
      const errors = validateForm();
      setFormErrors(errors || {});

      errors && document.getElementById(`labelOf${Object.keys(errors)[0]}`)?.scrollIntoView({behavior: "smooth"})
      return !errors;
    };

    const getFormStatus = () => {
      const errors = validateForm();
      return !errors;
    };

    React.useImperativeHandle(ref, () => ({
      handleSubmit: handleSubmit,
      getFormStatus: getFormStatus,
    }));

    const renderInput = (
      name,
      placeholder,
      type = "text",
      colSize = 6,
      required = true,
      disabled = false,
      additionalclass = "",
      minDate,
      maxDate
    ) => (
      <Col md={colSize} className="mb-3">
        <Form.Group
          controlId={`form${name.charAt(0).toUpperCase() + name.slice(1)}`}
          className={`required-wrapper ${required ? "required-field" : ""}`}
        >
          <Form.Label id={`labelOf${name}`}>{placeholder}</Form.Label>
          <div className="form-control-wrapper">
            {type === "date" ? (
              <DatePicker
                minDate={minDate}
                maxDate={maxDate}
                id={`form${name}`}
                selected={formData[name]}
                onChange={(date) => handleDateChange(name, date)}
                onBlur={(e) => {
                  validateField(name)
                  handleDateBlur(name, e)}
                }
                onSelect={() => setTimeout(() => document.activeElement.blur(), 1)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className={`form-control form-control-custom ${
                  formErrors[name] ? "is-invalid" : ""
                }  ${additionalclass} `}
                wrapperClassName={`w-100 ${
                  formErrors[name] ? "is-invalid" : ""
                }  ${additionalclass} `}
              />
            ) : type === "textarea" ? (
              <Form.Control
                as="textarea"
                rows={5}
                placeholder={`${required ? "*" : ""}`}
                value={formData[name]}
                name={name}
                disabled={disabled}
                onChange={handleChange}
                onBlur={() => validateField(name)}
                className={`form-control-custom ${
                  formErrors[name] ? "is-invalid" : ""
                }  ${additionalclass} `}
              />
            ) : (
              <Form.Control
                type={type}
                value={formData[name]}
                name={name}
                disabled={disabled}
                onChange={handleChange}
                onBlur={() => {
                  validateField(name)
                  setTimeout(() => {
                    setClaimantAddressSuggestions([])
                    setEmployerAddressSuggestions([])
                  }, 250)
                }
                }
                className={`form-control-custom ${
                  formErrors[name] ? "is-invalid" : ""
                }  ${additionalclass} `}
              />
            )}

            {name === "ClaimantAddress" &&
              renderClaimantSuggestionsDropdown((sugg) =>
                handleSelectAddress(sugg, "ClaimantAddress")
              )}
            {name === "EmployerAddress" &&
              renderEmployerSuggestionsDropdown((sugg) =>
                handleSelectAddress(sugg, "EmployerAddress")
              )}

            {required && <span className="red-asterisk">*</span>}
            {formErrors[name] && (
              <Form.Control.Feedback type="invalid">
                {formErrors[name]}
              </Form.Control.Feedback>
            )}
          </div>
          {(name === "ClaimantEmail" || name === "Phone") && <p className="text-muted small">(Strongly Recommended for Social Canvassing, but not required)</p>}
        </Form.Group>
      </Col>
    );

    const [showSSN, setShowSSN] = useState(false);
    const [isTypePassword, setIsTypePassword] = useState(true);
    
    // Auto set SSN input type to 'text'
    useEffect(() => {
      setIsTypePassword(false)
    }, [])

    const renderMaskedInput = (
      name,
      placeholder,
      colSize = 6,
      required = true,
      additionalclass = ""
    ) => (
      <Col md={colSize} className="mb-3">
        <Form.Group
          controlId={`form${name.charAt(0).toUpperCase() + name.slice(1)}`}
          className={`required-wrapper ${required ? "required-field" : ""}`}
        >
          <Form.Label id={`labelOf${name}`}>{placeholder}</Form.Label>
          <div className="form-control-wrapper">
            <MaskedInput
              value={formData[name]}
              show={showSSN}
              id={`form${name}`}
              type={isTypePassword ? "password" : "text"}
              onChange={(value) => handleMaskedValueChange(name, value)}
              onBlur={() => validateField(name)}
              className={`form-control-custom form-control${
                formErrors[name] ? " is-invalid" : ""
              } ${additionalclass}`}
              maxLength="11"
              autoComplete="off"
            />
            <div className="toggle-view">
              {showSSN ? (
                <MdVisibilityOff onClick={() => setShowSSN(false)} />
              ) : (
                <MdVisibility onClick={() => setShowSSN(true)} />
              )}
            </div>
            {required && <span className="red-asterisk">*</span>}
            {formErrors[name] && (
              <Form.Control.Feedback type="invalid">
                {formErrors[name]}
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>
      </Col>
    );
    const renderDropdown = (
      name,
      placeholder,
      colSize = 6,
      required = true,
      disabled = false,
      additionalclass = "",
      options = []
    ) => (
      <Col md={colSize} className="mb-3">
        <Form.Group
          controlId={`form${name.charAt(0).toUpperCase() + name.slice(1)}`}
          className={`required-wrapper ${required ? "required-field" : ""}`}
        >
          <Form.Label id={`labelOf${name}`}>{placeholder}</Form.Label>
          <div className="form-control-wrapper">
            <Form.Control
              as="select"
              value={formData[name]}
              name={name}
              disabled={disabled}
              onChange={handleChange}
              onBlur={() => validateField(name)}
              className={`form-control-custom ${
                formErrors[name] ? "is-invalid" : ""
              } ${additionalclass}`}
            >
              <option value=""disabled hidden>{}</option>
              {options.map((option, index) => (
                <option key={index} value={option.label}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
            {required && <span className="red-asterisk">*</span>}
            {formErrors[name] && (
              <Form.Control.Feedback type="invalid">
                {formErrors[name]}
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>
      </Col>
    );
    const natureOptions = [
      { value: "brain_tbi", label: "Brain, TBI" },
      { value: "cancer", label: "Cancer" },
      {
        value: "cardiological_cardiovascular",
        label: "Cardiological, Cardiovascular",
      },
      { value: "ear_nose_throat", label: "Ear, Nose, Throat" },
      { value: "eye_vision", label: "Eye, Vision" },
      { value: "head_neck", label: "Head, Neck" },
      { value: "hearing", label: "Hearing" },
      {
        value: "lower_extremities",
        label: "Lower Extremities (Hip, Thigh, Knee, Leg, Ankle, Foot)",
      },
      { value: "mental_behavioral", label: "Mental & Behavioral" },
      {
        value: "multiple_extremities",
        label: "Multiple / Upper & Lower Extremities",
      },
      { value: "neck_back", label: "Neck, Back" },
      { value: "neck_back_shoulders", label: "Neck, Back, Shoulder(s)" },
      { value: "shoulders", label: "Shoulder(s)" },
      { value: "sprain_strain", label: "Sprain, Strain" },
      { value: "spine_low_back", label: "Spine, Low Back" },
      {
        value: "upper_extremities",
        label: "Upper Extremities (Arm, Forearm, Wrist, Hand)",
      },
      { value: "unknown", label: "Unknown" },
      { value: "other", label: "Other (Specify in Comments)" },
    ];

    return (
      <Container>
        <h1 className="txt-orange">Claimant Information</h1>
        <p className="p-style">
          Complete the following fields. Fields with an * are required.
        </p>
        <Form ref={ref} onSubmit={handleSubmit}>
          <Row>
            {renderInput("ClaimantFirstName", "Claimant First Name", "text", 3)}
            {renderInput("ClaimantLastName", "Claimant Last Name", "text", 3)}
            {renderInput(
              "Alias",
              "Alias(es) - List All that Apply",
              "text",
              6,
              false
            )}
            {renderMaskedInput("SSN", "Last 4 or 9 Digits of SSN", 6)}
            {renderInput(
              "DateBirth",
              "Date of Birth: MM/DD/YYYY",
              "date",
              6,
              true,
              false,
              null,
              null,
              today
            )}
            {renderInput("ClaimantAddress", "Claimant Address", "text", 12)}
            {renderInput("city", "City", "text", 4)}
            {renderInput("state", "State", "text", 4)}
            {renderInput("zip", "Zip", "text", 4)}
            {renderInput(
              "ClaimantEmail",
              "Claimant Email",
              "text",
              6,
              false,
              false,
            )}
            {renderInput(
              "Phone",
              "Phone",
              "tel",
              6,
              false,
              false,
            )}
            {renderInput("EmployerName", "Employer Name", "text", 12, false)}
            {renderInput(
              "EmployerAddress",
              "Employer Address",
              "text",
              12,
              false
            )}
            {renderInput("EmployerCity", "City", "text", 4, false)}
            {renderInput("EmployerState", "State", "text", 4, false)}
            {renderInput("EmployerZip", "Zip", "text", 4, false)}
            {renderInput("EmployerPhone", "Employer Phone", "tel", 6, false)}
            {renderInput(
              "EmployerContactName",
              "Employer Contact Name",
              "text",
              6,
              false
            )}
            {renderInput(
              "ClaimantPosition",
              "Claimant's Position",
              "text",
              4,
              false
            )}
            {renderInput(
              "DateIncident",
              "Date of Incident: MM/DD/YYYY",
              "date",
              4,
              true,
              false,
              null,
              null,
              today
            )}
            {renderInput(
              "LastWorked",
              "Last Worked: MM/DD/YYYY",
              "date",
              4,
              false,
              false,
              null,
              null,
              today
            )}
            {renderDropdown(
              "Nature",
              "Nature/Extent of Alleged Injury/Loss",
              12,
              true,
              false,
              "",
              natureOptions
            )}
            {renderInput("CoverageType", "Coverage Type", "text", 6, false)}
            {renderInput(
              "DueDate",
              "Due Date: MM/DD/YYYY",
              "date",
              6,
              false,
              false,
              null,
              today,
              null
            )}
            <FileInput
              uploadedFilesNames={
                uploadedFiles?.claimantInfo?.length > 0
                  ? uploadedFiles?.claimantInfo?.map((file) => file.name)
                  : undefined
              }
              onUploadFile={(file) =>
                onUploadFile({
                  ...uploadedFiles,
                  claimantInfo: (uploadedFiles?.claimantInfo?.length > 0
                    ? [...uploadedFiles?.claimantInfo]
                    : []
                  ).concat([file]),
                })
              }
              onRemoveFile={(index) =>
                onUploadFile({
                  ...uploadedFiles,
                  claimantInfo: uploadedFiles?.claimantInfo?.filter(
                    (_, i) => i !== index
                  ),
                })
              }
              askForUplaodText="Upload any Relevant Documentation (Medical Authorization, Previous Canvass Reports, etc)."
              isRequired={false}
            />

            {renderInput(
              "AdditionalComments",
              "Additional Comments",
              "textarea",
              12,
              false
            )}
          </Row>
          <Container>
            <div className="mb-3">
              <span className="d-flex align-items-center">
                <span className="arrow-img">
                  <img src={arrow} alt="arrow" />
                </span>

                <a
                  href="https://intertelinc.com/privacy-policy/"
                  className="terms-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </span>
            </div>
            <div className="mb-3">
              <span className="d-flex align-items-center">
                <span className="arrow-img">
                  <img src={arrow} alt="arrow" />
                </span>
                <a
                  href="https://intertelinc.com/terms-conditions/"
                  className="terms-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </a>
              </span>
            </div>
          </Container>
        </Form>
      </Container>
    );
  }
);

export default ClaimantForm;
